import FileUploadMultiple from "components/FileUploadMultiple";
import { useTooltip } from "context/TooltipProvider";
import React, { FC, useEffect, useState } from "react";
import { addEvidence, editEvidence } from "services/incidentService";
import { CrimeReference, Evidence, Investigation } from "types";
import { LoadingIcon } from "utils/constant";
import { errorHandlerOnForm } from "utils/helper";

type Props = {
  isOpen: boolean;
  action: "Add" | "Edit";
  investigation: Investigation;
  incident: CrimeReference | undefined;
  evidence?: Evidence;
  onClose: (refetch: boolean) => void;
};

const defaultEvidence: Evidence = {
  uu_id: 0,
  evidence_type: "",
  description: "",
  // Left empty so the required "Date Received" must be chosen explicitly.
  date_collected: undefined,
  collected_by: "",
  investigation: null,
  file_attachments: [],
  attachment_details: [],
};

const EvidenceForm: FC<Props> = ({
  isOpen,
  action,
  investigation,
  incident,
  evidence,
  onClose,
}) => {
  const { showTooltip } = useTooltip();
  const [loading, setLoading] = useState(false);
  const [formState, setFormState] = useState<Evidence>(
    evidence || defaultEvidence,
  );

  useEffect(() => {
    if (evidence && (investigation || incident)) {
      setFormState(evidence);
    }
  }, [evidence, investigation, incident]);

  const handleClickOutside = (e: any) => {
    if (e.target.classList.contains("fixed")) {
      onClose(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<
      HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement
    >,
  ) => {
    const { name, value } = e.target;
    setFormState((prevState) => ({
      ...prevState,
      [name]: value,
    }));
  };

  const validateForm = () => {
    // if (!formState.evidence_type) return "Evidence Type is Required.";
    // else if (!formState.collected_by) return "Collected By is Required.";
    if (!formState.date_collected) return "Date Received is Required.";

    const received = new Date(formState.date_collected);
    if (isNaN(received.getTime())) return "Date Received is invalid.";
    const endOfToday = new Date();
    endOfToday.setHours(23, 59, 59, 999);
    if (received.getTime() > endOfToday.getTime())
      return "Date Received cannot be in the future.";

    if (
      !formState.file_attachments ||
      formState.file_attachments.length === 0
    )
      return "File Attachments is Required.";

    return "";
  };

  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();

    const error = validateForm();
    if (error) {
      showTooltip(error, "error");
      return;
    }

    setLoading(true);
    const submitAction = action === "Edit" ? editEvidence : addEvidence;

    submitAction({
      ...formState,
      investigation: investigation?.uu_id,
      crimereference: incident ? (incident?.uu_id as number) : null,
      Incident_Registration_number:
        (incident?.Incident_Registration_number?.toString() || "") as any,
    })
      .then(() => {
        showTooltip("Successfully Updated!", "success");
        setFormState(defaultEvidence);
        onClose(true);
      })
      .catch((error) => {
        showTooltip(
          error?.response?.data?.detail || errorHandlerOnForm(error),
          "error",
        );
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const handleFile = (file: File | File[] | null) => {
    setFormState({
      ...formState,
      file_attachments: file ? (Array.isArray(file) ? file : [file]) : [],
    });
  };

  if (!isOpen) return null;

  return (
    <div
      style={{ marginTop: "0px" }}
      className="fixed inset-0 z-50 flex justify-end bg-black bg-opacity-50 backdrop-blur-sm p-4"
      onClick={handleClickOutside}
    >
      <div className="relative w-full max-w-2xl bg-white p-8 shadow-lg rounded-lg overflow-y-auto">
        <h2 className="mb-1 text-2xl font-semibold">Add Evidence File Item</h2>
        <p className="mb-6 text-xs font-light text-[#9D9D9D]">
          publishing industries as a temporary filler.
        </p>

        <form className="space-y-4" onSubmit={handleSubmit}>
          {/* <div className="flex flex-col mt-4">
            <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
              Evidence Type
            </label>
            <select
              required
              className="border border-gray-300 rounded-lg bg-[#F2F2F2] p-2 pr-8 focus:outline-none focus:ring focus:ring-blue-300"
            >
              <option value="">Select Type</option>
              <option value="photo">Photo</option>
              <option value="document">Document</option>
              <option value="audio">Audio</option>
            </select>
          </div> */}

          <div className="flex flex-col mt-4 w-full">
            <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
              Evidence Type
            </label>
            <input
              type="text"
              className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
              name="evidence_type"
              value={formState?.evidence_type}
              onChange={handleChange}
            />
          </div>
          <div className="flex flex-col mt-4 w-full">
            <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
              Collected By
            </label>
            <input
              type="text"
              className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
              name="collected_by"
              value={formState?.collected_by}
              onChange={handleChange}
            />
          </div>
          <div className="flex flex-col mt-4">
            <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
              Date Received <span className="text-red-500">*</span>
            </label>
            <input
              type="date"
              required
              max={new Date().toISOString().split("T")[0]}
              className="bg-[#F2F2F2] rounded-lg p-2 focus:outline-none focus:ring focus:ring-blue-300"
              name="date_collected"
              value={
                formState?.date_collected
                  ? new Date(formState.date_collected)
                      .toISOString()
                      .split("T")[0]
                  : ""
              }
              onChange={handleChange}
            />
          </div>

          <div>
            <label className="text-sm text-[#747171] font-[inter] font-[400] mb-2">
              Description
            </label>
            <textarea
              rows={4}
              className="w-full rounded-md border bg-[#F2F2F2] p-2 focus:border-blue-500 focus:outline-none focus:ring"
              name="description"
              value={formState?.description}
              onChange={handleChange}
            />
          </div>

          <div className="mt-4">
            <FileUploadMultiple
              label="Attachment Files"
              fileTypes="*"
              info="Upload PDF Files"
              multiple={true}
              required={true}
              onChange={handleFile}
            />
          </div>

          <div className="text-right">
            <button
              type="submit"
              className={`rounded-md bg-[#0A1D39] px-8 py-3 text-white font-normal text-sm 
                hover:bg-[#112D5A] transition
                          ${loading ? "opacity-50 cursor-not-allowed" : ""}`}
              disabled={loading}
            >
              {loading ? (
                <div className="flex items-center justify-center">
                  {LoadingIcon}
                  Loading...
                </div>
              ) : (
                action + " Evidence"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EvidenceForm;
